import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Copy, Power } from "lucide-react";

export default function ApiKeys() {
    const [keys, setKeys] = useState([]);
    const [open, setOpen] = useState(false);
    const [name, setName] = useState("");
    const [newKey, setNewKey] = useState(null);

    const load = async () => setKeys((await api.get("/admin/api-keys")).data);
    useEffect(() => { load(); }, []);

    const onCreate = async (e) => {
        e.preventDefault();
        try {
            const { data } = await api.post("/admin/api-keys", { name });
            setNewKey(data);
            setName("");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };
    const onRevoke = async (id) => {
        if (!window.confirm("Revoke this API key?")) return;
        await api.delete(`/admin/api-keys/${id}`);
        toast.success("Revoked"); load();
    };
    const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copied"); };

    const closeAll = () => { setOpen(false); setNewKey(null); };

    return (
        <div className="p-8 max-w-5xl" data-testid="api-keys-page">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <div className="text-xs tracking-widest text-[#7A726D] uppercase">Integration</div>
                    <h1 className="font-display text-4xl font-bold text-espresso tracking-tight">API keys</h1>
                    <p className="text-sm text-[#7A726D] mt-1 max-w-2xl">
                        Issue keys for external apps. Send the key in the <code className="bg-[#F3EFEA] px-1 rounded">X-API-Key</code> header.
                        Validate a voucher via <code className="bg-[#F3EFEA] px-1 rounded">GET /api/v1/voucher/{`{serial}`}</code> and redeem via <code className="bg-[#F3EFEA] px-1 rounded">POST /api/v1/voucher/{`{serial}`}/redeem</code>.
                    </p>
                </div>
                <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setNewKey(null); }}>
                    <DialogTrigger asChild><Button className="bg-espresso rounded-sm" data-testid="new-key-button"><Plus className="w-4 h-4 mr-1"/>New key</Button></DialogTrigger>
                    <DialogContent className="rounded-sm">
                        <DialogHeader><DialogTitle className="font-display text-espresso">{newKey ? "Key created" : "Create API key"}</DialogTitle></DialogHeader>
                        {newKey ? (
                            <div className="space-y-3" data-testid="new-key-display">
                                <p className="text-sm text-[#B24A45]">Copy this key now — it will not be shown again.</p>
                                <div className="bg-[#F3EFEA] p-3 rounded-sm font-mono text-xs break-all" data-testid="new-key-value">{newKey.api_key}</div>
                                <div className="flex gap-2">
                                    <Button onClick={() => copy(newKey.api_key)} className="bg-espresso rounded-sm flex-1" data-testid="copy-key-button"><Copy className="w-4 h-4 mr-2"/>Copy</Button>
                                    <Button onClick={closeAll} variant="outline" className="rounded-sm" data-testid="key-done-button">Done</Button>
                                </div>
                            </div>
                        ) : (
                            <form onSubmit={onCreate} className="space-y-3" data-testid="key-form">
                                <div><Label className="text-xs uppercase text-[#7A726D]">Key name</Label><Input value={name} onChange={(e) => setName(e.target.value)} required placeholder="e.g. Loyverse-integration" className="rounded-sm mt-1" data-testid="key-name-input"/></div>
                                <Button type="submit" className="bg-espresso w-full rounded-sm" data-testid="key-submit-button">Generate</Button>
                            </form>
                        )}
                    </DialogContent>
                </Dialog>
            </div>
            <Card className="border-[#E5E0D8] shadow-none rounded-sm">
                <CardContent className="p-0">
                    <Table data-testid="keys-table">
                        <TableHeader><TableRow className="bg-[#F9F8F6]"><TableHead>Name</TableHead><TableHead>Prefix</TableHead><TableHead>Created</TableHead><TableHead>Last used</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
                        <TableBody>
                            {keys.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-[#7A726D] py-8">No keys yet</TableCell></TableRow>}
                            {keys.map((k) => (
                                <TableRow key={k.id}>
                                    <TableCell className="font-medium">{k.name}</TableCell>
                                    <TableCell className="font-mono text-xs">{k.prefix}…</TableCell>
                                    <TableCell className="text-xs">{k.created_at?.slice(0, 10)}</TableCell>
                                    <TableCell className="text-xs">{k.last_used_at ? k.last_used_at.slice(0, 19).replace("T", " ") : "Never"}</TableCell>
                                    <TableCell>{k.is_active ? <span className="text-sage text-xs uppercase">Active</span> : <span className="text-[#7A726D] text-xs uppercase">Revoked</span>}</TableCell>
                                    <TableCell className="text-right">
                                        {k.is_active && <button onClick={() => onRevoke(k.id)} className="text-[#B24A45] text-sm" data-testid={`revoke-key-${k.id}`}><Power className="w-4 h-4"/></button>}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </div>
    );
}
